import { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MenuList, MenuListDivider, ListItem } from '@clickguard/design-system'
import { useStores } from 'hooks'
import { useWorkspaceAccess } from 'hooks/use-workspace-access'
import { useAccountStore, useAssetStore, useUiStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { ModalTypes } from 'components/basic-blocks/g-modal/types'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { AddMemberModalBody } from 'pages/workspace/team-access/add-member/add-member'
import { ConnectAdAccountComponent } from 'components/setup-v2/connect-ad-account-component'
import Plus from '@untitled-ui/icons-react/build/esm/Plus'
import Globe02 from '@untitled-ui/icons-react/build/esm/Globe02'
import Dataflow02 from '@untitled-ui/icons-react/build/esm/Dataflow02'
import UserPlus01 from '@untitled-ui/icons-react/build/esm/UserPlus01'

export const PlusButtonOverflow = () => {
	const navigate = useNavigate()
	const { accountStore, userStore } = useStores()
	const { user } = userStore
	const { account } = useAccountStore(
		useShallow((s) => ({ account: s.account }))
	)
	const { asset, demoMode, refreshAdAccounts } = useAssetStore(
		useShallow((s) => ({
			asset: s.asset,
			demoMode: s.demoMode,
			refreshAdAccounts: s.refreshAdAccounts
		}))
	)
	const setModalContent = useUiStore((s) => s.setModalContent)
	const setModalState = useUiStore((s) => s.setModalState)
	const trackPostHogEvent = useTrackPostHogEvent()

	const [open, setOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!open) return
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setOpen(false)
			}
		}
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false)
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [open])

	const accountId = demoMode && !account?.id ? 'workspace-demo' : account?.id
	const userAccess = accountStore.userAccessLevel(user?.id || '')
	const isOwner = userAccess === 'OWNER'
	const isManagerOrOwner = userAccess === 'OWNER' || userAccess === 'WRITE'
	// Case 3 (no ad account): only "Invite members" and "Create a new workspace".
	const { access } = useWorkspaceAccess()
	const canAddToWorkspace = isManagerOrOwner && access === 'full'

	const handleProtectWebsite = () => {
		trackPostHogEvent(
			PostHogEvent.CREATE_NEW_BUTTON,
			PostHogPath.GLOBAL,
			{ action: 'protect_website' }
		)
		if (accountId && accountId !== 'workspace-demo') {
			navigate(`/setup/${accountId}`)
		}
		setOpen(false)
	}

	const handleConnectAdAccount = () => {
		trackPostHogEvent(
			PostHogEvent.CREATE_NEW_BUTTON,
			PostHogPath.GLOBAL,
			{ action: 'connect_ad_account' }
		)
		setOpen(false)
		if (!account || !asset || !user || accountId === 'workspace-demo') return
		setModalContent({
			type: ModalTypes.INFO,
			actionsEnabled: false,
			showIcon: false,
			rootClassName: 'bg-transparent',
			body: (
				<ConnectAdAccountComponent
					account={account}
					isOnboarding={false}
					user={user}
					asset={asset}
					onSuccess={async () => {
						await refreshAdAccounts()
						setModalState(false)
					}}
				/>
			)
		})
	}

	const handleInviteMembers = () => {
		trackPostHogEvent(
			PostHogEvent.CREATE_NEW_BUTTON,
			PostHogPath.GLOBAL,
			{ action: 'invite_member' }
		)
		setModalContent({
			title: 'Add Member',
			type: ModalTypes.INFO,
			actionsEnabled: false,
			showIcon: false,
			body: <AddMemberModalBody />
		})
		setOpen(false)
	}

	const handleCreateWorkspace = () => {
		trackPostHogEvent(
			PostHogEvent.CREATE_NEW_BUTTON,
			PostHogPath.GLOBAL,
			{ action: 'create_workspace' }
		)
		navigate('/start/standard')
		setOpen(false)
	}

	return (
		<div className="relative" ref={containerRef}>
			<button
				onClick={() => setOpen((prev) => !prev)}
				className="flex items-center justify-center w-[32px] h-[32px] rounded-[9.16px] bg-[#554bfd] border-t-[0.75px] border-r-[0.5px] border-l-[0.5px] border-solid border-[#8f88ff] shadow-[0px_2.443px_6.107px_2.443px_rgba(0,0,0,0.05)] backdrop-blur-[3.053px] shrink-0 cursor-pointer"
			>
				<Plus width={20} height={20} className="text-[rgb(var(--cg-text-on-brand))]" />
			</button>

			{open && (
				<div className="absolute left-0 top-full mt-2 z-50">
					<MenuList className="w-[276px]">
						{canAddToWorkspace && (
							<ListItem
								leadingIcon={<Globe02 width={20} height={20} />}
								label="Protect a new website"
								onClick={handleProtectWebsite}
							/>
						)}
						{canAddToWorkspace && (
							<ListItem
								leadingIcon={<Dataflow02 width={20} height={20} />}
								label="Connect a new ad account"
								onClick={handleConnectAdAccount}
							/>
						)}
						{isOwner && (
							<ListItem
								leadingIcon={<UserPlus01 width={20} height={20} />}
								label="Invite members"
								onClick={handleInviteMembers}
							/>
						)}
						{(canAddToWorkspace || isOwner) && <MenuListDivider />}
						<ListItem
							leadingIcon={<Plus width={20} height={20} />}
							label="Create a new workspace"
							onClick={handleCreateWorkspace}
						/>
					</MenuList>
				</div>
			)}
		</div>
	)
}
